/** The 2FA step of password sign-in (port of auth/verify_2fa.html —
 *  the last unported auth page, Phase 5 of the port).
 *
 * A real form POST to Flask, like Login: the browser follows the
 * cookie-setting redirect on success, and failures come back as
 * /verify-2fa?error=<missing|expired|invalid> after the server-side
 * anti-brute-force delay. The pending email comes from
 * /auth/2fa-state (the session cookie carries the pending flag across
 * the redirect); no pending verification bounces to /login, exactly
 * as the Flask page did.
 */
import { useEffect, useState } from 'react'

import { API_BASE } from '../../lib/api'
import './auth.css'

const ERRORS: Record<string, string> = {
  missing: 'No verification code found. Please request a new one.',
  expired: 'Verification code has expired. Please request a new one.',
  invalid: 'Invalid verification code. Please try again.',
  'resend-failed': 'Failed to send verification code. Please try again.',
}

export default function Verify2FA() {
  const [email, setEmail] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const params = new URLSearchParams(window.location.search)
  const error = params.get('error')
  const resent = params.get('resent')

  useEffect(() => {
    fetch(`${API_BASE}/auth/2fa-state`, { credentials: 'include' })
      .then((r) => r.json())
      .then((j: { pending: boolean; email: string | null }) => {
        if (!j.pending) { window.location.replace('/login'); return }
        setEmail(j.email)
      })
      .catch(() => window.location.replace('/login'))
  }, [])

  if (email === null) return null // resolving pending state (or redirecting)

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">Priori Sports</div>
        <h1 className="auth-title">Two-Factor Authentication</h1>
        <p className="auth-sub">
          A verification code has been sent to <strong>{email}</strong>
        </p>

        {error && (
          <div className="auth-flash auth-flash--error">
            {ERRORS[error] ?? 'Verification failed. Please try again.'}
          </div>
        )}
        {resent && (
          <div className="auth-flash auth-flash--info">
            A new verification code has been sent to your email.
          </div>
        )}

        <form action={`${API_BASE}/auth/verify-2fa`} method="POST">
          <label className="auth-field">
            <span className="auth-label">Enter verification code</span>
            <input className="auth-input auth-input--code" name="code"
                   inputMode="numeric" autoComplete="one-time-code"
                   placeholder="6-digit code" required autoFocus
                   value={code}
                   onChange={(e) =>
                     setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} />
          </label>
          <button className="auth-btn auth-btn--primary" type="submit">Verify</button>
        </form>

        <form action={`${API_BASE}/auth/resend-code`} method="POST">
          <button className="auth-btn auth-btn--outline" type="submit">
            Resend Code
          </button>
        </form>

        <p className="auth-sub"><a href="/login">Back to login</a></p>
      </div>
    </div>
  )
}
